import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AuthModalField } from '@/components/auth/AuthModalField'
import { AuthPrimaryButton } from '@/components/auth/AuthPrimaryButton'
import { useAuthModal } from '@/context/AuthModalContext'
import { useLoader } from '@/context/LoaderContext'
import { forgotPasswordSchema, type ForgotPasswordFormValues } from '@/lib/auth-validation'
import { useForgotPasswordMutation } from '@/store/api/authApi'

export function ForgotPasswordForm() {
	const { setView } = useAuthModal()
	const { showLoader, hideLoader } = useLoader()
	const [forgotPassword, { isLoading }] = useForgotPasswordMutation()
	const [isSubmitted, setIsSubmitted] = useState(false)
	const [submittedEmail, setSubmittedEmail] = useState('')

	useEffect(() => {
		if (!isLoading) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [isLoading, showLoader, hideLoader])

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<ForgotPasswordFormValues>({
		resolver: zodResolver(forgotPasswordSchema),
		defaultValues: {
			email: '',
		},
	})

	const onSubmit = async (values: ForgotPasswordFormValues) => {
		try {
			await forgotPassword({ email: values.email }).unwrap()
			setSubmittedEmail(values.email)
			setIsSubmitted(true)
		} catch {
			// Error toast is shown globally by the API interceptor.
		}
	}

	if (isSubmitted) {
		return (
			<div className='flex flex-col gap-4'>
				<div className='space-y-4'>
					<h2 className='text-xl font-semibold tracking-[-0.24px] text-foreground'>Check your email</h2>
					<p className='text-sm leading-[140%] tracking-[-0.21px] text-muted-foreground'>
						If an account exists for <span className='font-medium text-foreground'>{submittedEmail}</span>, you will receive a password reset link shortly.
					</p>
				</div>

				<AuthPrimaryButton label='Back to login' type='button' onClick={() => setView('login')} />
			</div>
		)
	}

	return (
		<form noValidate className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)}>
			<div className='space-y-4'>
				<h2 className='text-xl font-semibold tracking-[-0.24px] text-foreground'>Forgot password?</h2>
				<p className='text-sm leading-[140%] tracking-[-0.21px] text-muted-foreground'>
					Enter the email associated with your account and we&apos;ll send you a link to reset your password.
				</p>
			</div>

			<AuthModalField type='email' placeholder='Email address' autoComplete='email' error={errors.email?.message} {...register('email')} />

			<AuthPrimaryButton label='Send reset link' type='submit' isLoading={isLoading} />

			<button type='button' onClick={() => setView('login')} className='text-sm font-medium tracking-[-0.21px] text-foreground transition-opacity hover:opacity-80'>
				Back to login
			</button>
		</form>
	)
}
